import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterOfficialUnits, getLotDetail } from '../../services/lotsService';
import { toast } from '../../Components/Toast';
import { CORDOBA_PROVINCE_COVERAGE_BBOX } from '../data/cordobaProvinceCoverage';
import { OFFICIAL_UNIT_FILTER_MIN_ZOOM } from '../styles/mapTheme';
import { fitMapToLotBounds } from '../utils/fitMapBoundsToLots';
import {
  isLotViewQuotaForbiddenResult,
  parseLotViewQuotaFromHttpResult,
} from '../utils/lotViewQuota';
import {
  bboxContains,
  buildOfficialUnitFilterBody,
  buildOfficialUnitFilterCriteria,
  isOfficialUnitLocatorQuery,
  isOfficialUnitNomenclatureOnlyQuery,
  formatOfficialUnitZoomHint,
  officialUnitCriteriaKey,
  padBbox,
  readMapBbox,
  readMapZoom,
} from '../utils/officialUnitFilterRequest';

const DEBOUNCE_MS = 300;
/** Margen extra sobre la vista: un paneo corto no vuelve a pedir. */
const BBOX_PADDING_RATIO = 0.35;

/** Estados que el panel traduce a copy. */
export const OFFICIAL_UNIT_FILTER_STATUS = {
  idle: 'idle',
  zoom: 'zoom',
  loading: 'loading',
  ready: 'ready',
  empty: 'empty',
  truncated: 'truncated',
  error: 'error',
};

const IDLE_STATE = {
  status: OFFICIAL_UNIT_FILTER_STATUS.idle,
  matchCount: null,
  unitCount: null,
};

/** Recorte activo sin ids que mostrar (cargando, zoom lejos, truncado, error). */
const NO_MATCHES = [];

const sameLotIds = (prev, next) => {
  if (prev === next) return true;
  if (prev == null || next == null) return prev == null && next == null;
  if (prev.length !== next.length) return false;
  return prev.every((id, index) => id === next[index]);
};

const readCounts = (data) => {
  const matchCount = Number(data?.match_count);
  const unitCount = Number(data?.unit_count);
  if (!Number.isFinite(matchCount) || !Number.isFinite(unitCount)) return null;
  if (matchCount < 0 || unitCount < 0) return null;
  return { matchCount, unitCount };
};

/** Punto representativo del POST (`data.lots[]`, mismo orden que `lot_ids`). */
const readLotCenter = (data, lotId) => {
  const rows = Array.isArray(data?.lots) ? data.lots : [];
  const wanted = String(lotId);
  const row = rows.find((item) => String(item?.id || '') === wanted)
    || (rows.length === 1 ? rows[0] : null);
  if (!row) return null;
  const lat = Number(row.lat);
  const lng = Number(row.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { id: wanted, lat, lng };
};

/**
 * Recorte del mapa por UF catastrales: `POST /filterofficialunits` → `lot_ids`
 * para hacer AND con el recorte de parcela. El GET por lote no se usa acá.
 *
 * Cómo evita pedir de más:
 * - El bbox se pide con un margen extra; mientras la vista siga adentro y los
 *   criterios no cambien, se reusan los ids sin tocar la red.
 * - Bajo `OFFICIAL_UNIT_FILTER_MIN_ZOOM` no se pide nada: el bbox cubre una
 *   ciudad y los puntos de parcela son una muestra que no representa el padrón.
 * - Si `q` tiene ≥ 8 caracteres, el bbox es Córdoba (no la vista) y, con un
 *   solo lote, se encuadra con `data.lots[].lat/lng` (ST_PointOnSurface).
 *
 * @param {object} options
 * @param {{ current: object|null }} options.mapInstanceRef
 * @param {Record<string, unknown>} options.activeFilters
 * @param {boolean} [options.enabled]
 * @param {string|null} [options.province]
 * @param {(ids: string[]|null) => void} options.onLotIdsChange
 *   `null` = sin recorte por UF. `[]` = recorte activo sin ids que mostrar.
 * @param {(quota: object) => void} [options.onQuotaInfo]
 * @returns {{ status: string, matchCount: number|null, unitCount: number|null, retry: function }}
 */
export function useOfficialUnitMapFilter({
  mapInstanceRef,
  activeFilters,
  enabled = true,
  province = null,
  onLotIdsChange,
  onQuotaInfo,
}) {
  const [state, setState] = useState(IDLE_STATE);
  const [retryTick, setRetryTick] = useState(0);

  const onChangeRef = useRef(onLotIdsChange);
  onChangeRef.current = onLotIdsChange;
  const onQuotaInfoRef = useRef(onQuotaInfo);
  onQuotaInfoRef.current = onQuotaInfo;
  const lotIdsRef = useRef(null);
  /** Última respuesta completa: `{ key, bbox, ids, counts }`. */
  const cacheRef = useRef(null);
  const toastedStatusRef = useRef(null);
  /** Parcelas del último `ready`: un solo lote no merece el aviso de zoom. */
  const lastReadyLotCountRef = useRef(0);
  /** Una encuadre por clave de criterios: el flyTo no debe repetirse al panear. */
  const locatedForKeyRef = useRef(null);

  const criteria = useMemo(
    () => (enabled ? buildOfficialUnitFilterCriteria(activeFilters) : null),
    [enabled, activeFilters],
  );
  const criteriaKey = useMemo(
    () => officialUnitCriteriaKey(criteria, province),
    [criteria, province],
  );
  const criteriaRef = useRef(criteria);
  criteriaRef.current = criteria;
  const provinceRef = useRef(province);
  provinceRef.current = province;

  const retry = useCallback(() => setRetryTick((tick) => tick + 1), []);

  useEffect(() => {
    // Reintento manual: vuelve a pedir aunque no cambien criterios ni vista.
    void retryTick;

    const publish = (next) => {
      if (sameLotIds(lotIdsRef.current, next)) return;
      lotIdsRef.current = next;
      onChangeRef.current?.(next);
    };

    /**
     * El panel muestra todos los estados; el toast solo avisa cuando el mapa se
     * vacía por algo que el usuario puede no tener a la vista. Se emite una vez
     * por transición y `loading` no cuenta, así panear con el zoom lejos o con
     * el backend caído no repite la notificación en cada movimiento. El de zoom
     * no sale si solo busca nomenclatura/cuenta o si ya hay un solo lote.
     */
    const publishState = (status, counts = null) => {
      setState((prev) => {
        const matchCount = counts ? counts.matchCount : null;
        const unitCount = counts ? counts.unitCount : null;
        if (prev.status === status && prev.matchCount === matchCount && prev.unitCount === unitCount) {
          return prev;
        }
        return { status, matchCount, unitCount };
      });

      if (status === OFFICIAL_UNIT_FILTER_STATUS.ready) {
        lastReadyLotCountRef.current = counts?.matchCount
          ?? lotIdsRef.current?.length
          ?? 0;
      } else if (status === OFFICIAL_UNIT_FILTER_STATUS.idle) {
        lastReadyLotCountRef.current = 0;
      }

      if (status === OFFICIAL_UNIT_FILTER_STATUS.loading) return;
      if (toastedStatusRef.current === status) return;
      toastedStatusRef.current = status;
      if (status === OFFICIAL_UNIT_FILTER_STATUS.zoom) {
        const skipZoomToast = isOfficialUnitNomenclatureOnlyQuery(criteriaRef.current)
          || lastReadyLotCountRef.current === 1;
        if (!skipZoomToast) {
          toast.info(formatOfficialUnitZoomHint({ inMap: true }));
        }
      } else if (status === OFFICIAL_UNIT_FILTER_STATUS.error) {
        toast.error('No se pudo filtrar por unidades catastrales.');
      }
    };

    if (!criteriaRef.current) {
      cacheRef.current = null;
      locatedForKeyRef.current = null;
      publish(null);
      publishState(OFFICIAL_UNIT_FILTER_STATUS.idle);
      return undefined;
    }

    const map = mapInstanceRef.current;
    if (!map) return undefined;

    let debounceId = null;
    let controller = null;
    let cancelled = false;

    /** Ids vigentes para la vista actual sin pedir nada, o `null`. */
    const readCache = () => {
      const cache = cacheRef.current;
      if (!cache || cache.key !== criteriaKey) return null;
      const viewport = readMapBbox(map);
      if (!bboxContains(cache.bbox, viewport)) return null;
      return cache;
    };

    const applyCache = (cache) => {
      publish(cache.ids);
      publishState(
        cache.ids.length ? OFFICIAL_UNIT_FILTER_STATUS.ready : OFFICIAL_UNIT_FILTER_STATUS.empty,
        cache.counts,
      );
    };

    const run = async () => {
      const criteriaNow = criteriaRef.current;
      const locator = isOfficialUnitLocatorQuery(criteriaNow);

      if (!locator && readMapZoom(map) < OFFICIAL_UNIT_FILTER_MIN_ZOOM) {
        cacheRef.current = null;
        publish(NO_MATCHES);
        publishState(OFFICIAL_UNIT_FILTER_STATUS.zoom);
        return;
      }

      const viewport = readMapBbox(map);
      if (!viewport && !locator) return;

      // El mapa pudo volver a una zona ya pedida mientras corría el debounce.
      const cached = readCache();
      if (cached) {
        applyCache(cached);
        return;
      }

      const cache = cacheRef.current;
      const sameCriteria = cache?.key === criteriaKey;

      // Criterios nuevos: los ids anteriores son de otra consulta y no se
      // pueden dejar pintados. Paneando con los mismos criterios sí se
      // sostiene lo que ya está en pantalla mientras llega la respuesta.
      if (!sameCriteria) publish(NO_MATCHES);
      publishState(
        OFFICIAL_UNIT_FILTER_STATUS.loading,
        sameCriteria ? cache?.counts || null : null,
      );

      controller?.abort();
      controller = new AbortController();
      const { signal } = controller;

      const send = (bbox) => filterOfficialUnits(
        buildOfficialUnitFilterBody({ bbox, criteria: criteriaNow, province: provinceRef.current }),
        { signal },
      );

      try {
        let bbox = locator
          ? [...CORDOBA_PROVINCE_COVERAGE_BBOX]
          : padBbox(viewport, BBOX_PADDING_RATIO);
        let result = await send(bbox);
        if (cancelled || signal.aborted) return;

        // El margen extra amplía el área: si por eso se pasó del tope del
        // backend, la vista exacta todavía puede entrar. En búsqueda puntual
        // el bbox ya es la provincia: no tiene sentido achicar.
        if (!locator && result?._httpOk && result.data?.truncated) {
          bbox = viewport;
          result = await send(bbox);
          if (cancelled || signal.aborted) return;
        }

        if (!result?._httpOk) {
          if (isLotViewQuotaForbiddenResult(result)) {
            const quota = parseLotViewQuotaFromHttpResult(result);
            if (quota) onQuotaInfoRef.current?.(quota);
          }
          cacheRef.current = null;
          publish(NO_MATCHES);
          publishState(OFFICIAL_UNIT_FILTER_STATUS.error);
          return;
        }

        const data = result.data || {};
        if (data.truncated) {
          cacheRef.current = null;
          publish(NO_MATCHES);
          publishState(OFFICIAL_UNIT_FILTER_STATUS.truncated);
          return;
        }

        const ids = Array.isArray(data.lot_ids) ? data.lot_ids.map(String) : [];
        const counts = readCounts(data);
        cacheRef.current = { key: criteriaKey, bbox, ids, counts };
        publish(ids);
        publishState(
          ids.length ? OFFICIAL_UNIT_FILTER_STATUS.ready : OFFICIAL_UNIT_FILTER_STATUS.empty,
          counts,
        );

        // Un lote para esa nomenclatura/cuenta: vuela a `lots[0]`. Sin coords,
        // un GET de catastro. No abre el dock.
        if (criteriaNow?.q && ids.length === 1 && locatedForKeyRef.current !== criteriaKey) {
          locatedForKeyRef.current = criteriaKey;
          const fromPost = readLotCenter(data, ids[0]);
          const lot = fromPost || await getLotDetail(ids[0], { signal, retryOn429: 0 });
          if (cancelled || signal.aborted || !lot) return;
          fitMapToLotBounds(map, lot, { padding: 80, duration: 900, maxZoom: 18 });
        }
      } catch (error) {
        if (error?.name === 'AbortError' || cancelled) return;
        console.error('useOfficialUnitMapFilter: error filtrando UF oficiales', error);
        cacheRef.current = null;
        publish(NO_MATCHES);
        publishState(OFFICIAL_UNIT_FILTER_STATUS.error);
      }
    };

    const schedule = () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(run, DEBOUNCE_MS);
    };

    const handleMapIdle = () => {
      const locator = isOfficialUnitLocatorQuery(criteriaRef.current);
      if (!locator && readMapZoom(map) < OFFICIAL_UNIT_FILTER_MIN_ZOOM) {
        schedule();
        return;
      }
      const cache = readCache();
      if (!cache) {
        schedule();
        return;
      }
      // La vista sigue dentro de lo ya pedido: los ids no cambian.
      clearTimeout(debounceId);
      applyCache(cache);
    };

    schedule();
    map.on('moveend', handleMapIdle);
    map.on('zoomend', handleMapIdle);

    return () => {
      cancelled = true;
      clearTimeout(debounceId);
      controller?.abort();
      map.off('moveend', handleMapIdle);
      map.off('zoomend', handleMapIdle);
    };
  }, [mapInstanceRef, criteriaKey, retryTick]);

  return { ...state, retry };
}
